import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getPresence, repondrePresence } from '../services/presenceService';
import { formatDate } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import './Signature.css';

/**
 * Réponse à une convocation, sans connexion (migration 079).
 *
 * Les deux boutons de l'email mènent ici avec ?reponse=present|absent : la
 * réponse s'enregistre au chargement, le clic suffit. L'enregistrement passe
 * par le script de la page et non par le lien lui-même : un antivirus de
 * messagerie qui « visite » les liens ne répond donc pas à la place du
 * destinataire. On peut ensuite changer d'avis et laisser un mot.
 */
const LIBELLES = {
  present: 'Présence confirmée',
  absent: 'Absence signalée',
};

function joursTexte(jours = []) {
  return (jours || []).map((j) => formatDate(j)).join(', ');
}

function Presence() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const demandee = ['present', 'absent'].includes(params.get('reponse'))
    ? params.get('reponse')
    : null;

  const [info, setInfo] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [merci, setMerci] = useState(null);
  const dejaEnvoye = useRef(false);

  async function envoyer(reponse, mot = null) {
    setEnCours(true);
    setErreur(null);
    try {
      const res = await repondrePresence(token, reponse, mot);
      setInfo(res.data);
      setMerci(reponse);
    } catch (err) {
      setErreur(err.message || 'Réponse impossible.');
    } finally {
      setEnCours(false);
    }
  }

  useEffect(() => {
    let vivant = true;
    getPresence(token)
      .then((res) => {
        if (!vivant) return;
        setInfo(res.data);
        setCommentaire(res.data.commentaire || '');
        // Le bouton de l'email vaut réponse : enregistrée une seule fois.
        if (demandee && !dejaEnvoye.current && res.data.reponse !== demandee) {
          dejaEnvoye.current = true;
          envoyer(demandee);
        } else if (demandee && res.data.reponse === demandee) {
          setMerci(demandee);
        }
      })
      .catch((err) => vivant && setErreur(err.message || 'Lien invalide.'))
      .finally(() => vivant && setChargement(false));
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="devis-page devis-page-scroll signature-doc">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Votre convocation</h1>
        </div>
      </div>

      {chargement && <div className="signature-etat">Chargement…</div>}
      {!chargement && erreur && !info && (
        <div className="signature-etat signature-etat-erreur">{erreur}</div>
      )}

      {info && (
        <div className="devis-card" style={{ padding: 20, maxWidth: 640 }}>
          <p style={{ marginTop: 0 }}>
            {info.nom ? <strong>{info.nom}</strong> : null}
            {info.nom ? ', ' : ''}
            {info.role === 'formateur'
              ? 'vous êtes attendu(e) pour animer la formation :'
              : 'vous êtes convoqué(e) à la formation :'}
          </p>
          <div className="devis-form-section-title" style={{ marginTop: 4 }}>
            {info.intitule}
          </div>
          <div className="devis-contact-email">
            {info.societe}
            {info.jours?.length ? ` · ${joursTexte(info.jours)}` : ''}
            {info.heureDebut ? ` · ${info.heureDebut} – ${info.heureFin}` : ''}
            {info.lieu ? ` · ${info.lieu}` : ''}
          </div>

          {merci && (
            <div className="signature-etat signature-etat-succes" style={{ marginTop: 16 }}>
              <p>
                <strong>{LIBELLES[merci]}.</strong>{' '}
                {merci === 'present'
                  ? 'Merci, nous vous attendons.'
                  : 'Merci de nous avoir prévenus : l’équipe en est informée.'}
              </p>
            </div>
          )}
          {!merci && info.reponse && (
            <div className="devis-alert" style={{ marginTop: 16 }}>
              Réponse enregistrée : <strong>{LIBELLES[info.reponse]}</strong>
              {info.reponduLe ? ` le ${formatDate(info.reponduLe)}` : ''}.
            </div>
          )}
          {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

          <label className="devis-form-field" style={{ marginTop: 16 }}>
            <span>Un mot pour l’équipe (facultatif)</span>
            <textarea
              rows={3}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Retard prévu, remplaçant, question…"
              disabled={enCours}
            />
          </label>

          <div className="devis-modal-actions" style={{ justifyContent: 'flex-start' }}>
            <button
              type="button"
              className={`devis-btn ${info.reponse === 'present' ? 'devis-btn-envoye' : 'devis-btn-primary'}`}
              onClick={() => envoyer('present', commentaire)}
              disabled={enCours}
            >
              Je confirme ma présence
            </button>
            <button
              type="button"
              className="devis-btn devis-btn-danger"
              onClick={() => envoyer('absent', commentaire)}
              disabled={enCours}
            >
              Je ne pourrai pas être présent(e)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Presence;
